import { Controller } from "@hotwired/stimulus"
import { Tab, Modal } from "bootstrap"

const STEPS = [
  ["preparation", "Setup", "Confirm the aircraft, job, location and calculation configuration. Fields marked * are required before finalizing."],
  ["configuration", "Prepare", "Record the aircraft condition, then answer every preparation check. Explain each N/A response."],
  ["readings", "Weigh", "Enter the measured pitch and record at least two stable readings on every assigned scale."],
  ["corrections", "Corrections", "Reconcile the as-weighed aircraft with the empty-weight definition. Check each correction against its source."],
  ["final", "Final review", "Resolve outstanding requirements, review the saved result and identify the responsible engineer. Finalizing locks this weighing."]
]

export default class extends Controller {
  static targets = ["step", "next", "finalize", "previous", "stepCount", "guidance", "saveState", "readiness", "footerStatus"]
  static values = { unsaved: Boolean, initialStep: String, storageKey: { type: String, default: "ws-editor-tab" } }

  connect() {
    this.form = this.element.querySelector("form")
    this.savedState = this.formState()
    this.originalFooterText = this.hasFooterStatusTarget ? this.footerStatusTarget.textContent : ""
    this.originalSaveText = this.hasSaveStateTarget ? this.saveStateTarget.textContent : ""
    this.element.addEventListener("shown.bs.tab", this.onTabShown)
    this.element.addEventListener("input", this.onEdit)
    this.element.addEventListener("change", this.onEdit)
    this.element.addEventListener("invalid", this.onInvalid, true)
    this.form?.addEventListener("submit", this.onSubmit)
    this.form?.addEventListener("turbo:submit-end", this.onSubmitEnd)
    window.addEventListener("hashchange", this.restoreFromHashOrStorage)
    window.addEventListener("beforeunload", this.beforeUnload)
    document.addEventListener("turbo:before-visit", this.beforeVisit)
    this.restoreFromHashOrStorage()
    this.onEdit()
  }

  disconnect() {
    this.element.removeEventListener("shown.bs.tab", this.onTabShown)
    this.element.removeEventListener("input", this.onEdit)
    this.element.removeEventListener("change", this.onEdit)
    this.element.removeEventListener("invalid", this.onInvalid, true)
    this.form?.removeEventListener("submit", this.onSubmit)
    this.form?.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    window.removeEventListener("hashchange", this.restoreFromHashOrStorage)
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbo:before-visit", this.beforeVisit)
  }

  formState() {
    if (!this.form) return ""
    return JSON.stringify(Array.from(new FormData(this.form)).filter(([name, value]) =>
      !["workspace_step", "authenticity_token"].includes(name) && !name.endsWith("[moment]") && typeof value === "string"))
  }

  onEdit = () => {
    this.dirty = this.unsavedValue || this.formState() !== this.savedState
    this.element.classList.toggle("ws-editor--unsaved", this.dirty)
    if (this.hasSaveStateTarget) this.saveStateTarget.textContent = this.dirty
      ? "Unsaved changes · save to update results and check what is still required."
      : this.originalSaveText
    if (this.hasFooterStatusTarget) this.footerStatusTarget.textContent = this.dirty ? "Unsaved · save to recalculate" : this.originalFooterText
    this.readinessTargets.forEach((target) => { target.hidden = this.dirty })
  }

  beforeUnload = (event) => {
    if (!this.dirty || this.submitting) return
    event.preventDefault()
    event.returnValue = ""
  }

  beforeVisit = (event) => {
    if (this.dirty && !this.submitting && !window.confirm("Leave this weighing? Changes since your last save will be lost. Photos already uploaded are kept.")) event.preventDefault()
  }

  restoreFromHashOrStorage = () => {
    let stored
    try { stored = sessionStorage.getItem(this.storageKeyValue) } catch { /* Storage can be disabled. */ }
    const candidates = [window.location.hash.slice(1), this.hasInitialStepValue ? `pane-${this.initialStepValue}` : "", stored, "pane-preparation"]
    const target = candidates.find((id) => STEPS.some(([key]) => id === `pane-${key}`))
    this.showStep(target.replace("pane-", ""))
  }

  showStep(step) {
    if (!STEPS.some(([key]) => key === step)) return
    const button = this.element.querySelector(`[data-bs-target="#pane-${step}"]`)
    if (!button) return
    Tab.getOrCreateInstance(button).show()
    this.syncStep(`pane-${step}`)
  }

  goTo(event) {
    this.showStep(event.params.step)
    if (event.params.field) {
      const field = document.getElementById(event.params.field)
      field?.scrollIntoView({ block: "center" })
      field?.focus({ preventScroll: true })
    } else {
      this.element.querySelector(".ws-tabs")?.scrollIntoView({ block: "start" })
    }
  }

  previous() {
    const index = STEPS.findIndex(([key]) => key === this.stepTarget.value)
    if (index > 0) this.showStep(STEPS[index - 1][0])
  }

  onTabShown = (event) => {
    const id = event.target.getAttribute("data-bs-target")?.slice(1)
    if (!id) return
    const url = new URL(window.location.href)
    url.searchParams.delete("workspace_step")
    url.hash = id
    history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`)
    this.syncStep(id)
    this.element.querySelector(".ws-tabs")?.scrollIntoView({ block: "start" })
  }

  // Native validation may target a field in another tab or a closed scale
  // dialog. Reveal it rather than leaving an invisible invalid control.
  onInvalid = (event) => {
    event.preventDefault()
    if (this.invalidPending) return
    this.invalidPending = true
    const field = event.target
    const pane = field.closest(".tab-pane")
    if (pane) this.showStep(pane.id.replace("pane-", ""))
    const modal = field.closest(".modal")
    if (modal) Modal.getOrCreateInstance(modal).show()
    if (this.hasSaveStateTarget) this.saveStateTarget.textContent = `Check this field: ${field.validationMessage}`
    field.setAttribute("aria-invalid", "true")
    setTimeout(() => { field.focus(); this.invalidPending = false }, 350)
  }

  onSubmit = () => {
    this.submitting = true
    const active = this.element.querySelector(".ws-tab.active")
    const id = active?.getAttribute("data-bs-target")?.slice(1)
    if (!id) return
    try { sessionStorage.setItem(this.storageKeyValue, id) } catch { /* Saving must not depend on storage. */ }
    this.syncStep(id)
  }

  onSubmitEnd = (event) => {
    this.submitting = false
    if (event.detail.success) this.dirty = false
  }

  syncStep(id) {
    const step = id.replace(/^pane-/, "")
    const index = STEPS.findIndex(([key]) => key === step)
    if (index < 0) return
    if (this.hasStepTarget) this.stepTarget.value = step
    if (this.hasNextTarget) {
      this.nextTarget.hidden = step === "final"
      if (index < STEPS.length - 1) this.nextTarget.value = `Save → ${index === 3 ? "Review" : STEPS[index + 1][1]}`
    }
    if (this.hasFinalizeTarget) this.finalizeTarget.hidden = step !== "final"
    if (this.hasPreviousTarget) this.previousTarget.hidden = index === 0
    if (this.hasStepCountTarget) this.stepCountTarget.textContent = `Step ${index + 1} of ${STEPS.length}`
    if (this.hasGuidanceTarget) this.guidanceTarget.textContent = STEPS[index][2]
  }
}
